'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Plastic Recycling Numbers 1-7 Ranked: Microplastic & Chemical Safety"
      author="MicroPlastics Editorial Team"
      date="April 23, 2026"
      readTime="10 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="Plastic recycling numbers 1 through 7 ranked by safety"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        From safest to most concerning for food and beverage contact:
        <strong> #2 HDPE</strong>, <strong>#4 LDPE</strong>, and <strong>#5 PP</strong>
        are the least likely to leach harmful chemicals; <strong>#1 PET</strong> is
        common but degrades with heat and reuse; <strong>#7 (Other), #3 PVC, and #6
        polystyrene</strong> are the most concerning. <em>All</em> plastics shed
        microplastics (none are truly &ldquo;safe&rdquo;) so the safety ranking is
        relative, not absolute. For true microplastic avoidance, choose glass,
        stainless steel, or ceramic for food contact.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>The recycling number (1-7) is not a safety rating, it&apos;s a resin identification code for sorting.</>,
          <><strong>Safer for food</strong>: #2 HDPE, #4 LDPE, #5 PP.</>,
          <><strong>Acceptable but degrades with heat</strong>: #1 PET (single-use only, never refill or reheat).</>,
          <><strong>Avoid for food</strong>: #3 PVC, #6 polystyrene (styrofoam), #7 (Other, often polycarbonate / BPA).</>,
          <>Even the &ldquo;safest&rdquo; plastics shed microplastics with heat, UV, acidity, and physical wear. Glass and stainless steel are the only zero-microplastic options.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The full ranking
      </h2>

      <BlogComparisonTable
        caption="Plastic recycling numbers 1-7, safety ranked for food/beverage contact"
        columns={[
          { key: 'rank', label: 'Safety rank' },
          { key: 'number', label: '#' },
          { key: 'resin', label: 'Resin' },
          { key: 'common', label: 'Common products' },
          { key: 'concern', label: 'Main concern' },
        ]}
        rows={[
          { rank: '1 (safest)', number: '#5', resin: <Link href="/materials/polypropylene" className="text-[#1E6B47] hover:text-[#155235] underline">Polypropylene (PP)</Link>, common: 'Yogurt cups, takeout containers, straws, bottle caps', concern: 'Lowest leaching of common plastics. Some particle shedding under microwave heat.' },
          { rank: '2', number: '#2', resin: <Link href="/materials/hdpe" className="text-[#1E6B47] hover:text-[#155235] underline">High-density polyethylene (HDPE)</Link>, common: 'Milk jugs, juice bottles, detergent bottles', concern: 'Considered low-risk; does shed micro/nanoplastic with UV exposure.' },
          { rank: '3', number: '#4', resin: <Link href="/materials/ldpe" className="text-[#1E6B47] hover:text-[#155235] underline">Low-density polyethylene (LDPE)</Link>, common: 'Plastic bags, cling wrap, squeeze bottles', concern: 'Low chemical leaching; sheds fibres in food contact and laundry.' },
          { rank: '4', number: '#1', resin: <Link href="/materials/pet" className="text-[#1E6B47] hover:text-[#155235] underline">Polyethylene terephthalate (PET, PETE)</Link>, common: 'Water bottles, soda bottles', concern: 'Antimony leaching under heat; major microplastic source when reused or warmed.' },
          { rank: '5 (avoid where possible)', number: '#7', resin: <>Other (mixed; often <Link href="/materials/polycarbonate" className="text-[#1E6B47] hover:text-[#155235] underline">polycarbonate</Link>, BPA, BPS)</>, common: 'Reusable water bottles (older), 5-gal water jugs', concern: 'Often contains BPA or replacement bisphenols (BPS, BPF), known endocrine disruptors.' },
          { rank: '6 (avoid)', number: '#3', resin: <Link href="/materials/pvc" className="text-[#1E6B47] hover:text-[#155235] underline">Polyvinyl chloride (PVC)</Link>, common: 'Cling wrap, food packaging films, pipes', concern: 'Releases phthalates and vinyl chloride. PVC microplastics implicated in NEJM 2024 cardiovascular study.' },
          { rank: '7 (avoid)', number: '#6', resin: <Link href="/materials/polystyrene" className="text-[#1E6B47] hover:text-[#155235] underline">Polystyrene (PS, Styrofoam)</Link>, common: 'Takeout containers, cups, meat trays', concern: 'Releases styrene (IARC possible human carcinogen). High particle shedding under heat or contact with hot/oily food.' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why the recycling symbol doesn&apos;t mean what you think
      </h2>
      <p>
        The triangle of arrows with a number inside is a <em>Resin Identification
        Code</em> (RIC), introduced by the plastics industry in 1988 to help
        sorters at recycling facilities. It is not a safety rating, an indicator of
        recyclability in your community, or a guarantee that the item was made from
        recycled material.
      </p>
      <p>
        In practice, only #1 PET and #2 HDPE are widely recycled in most US and EU
        municipal programs. #3-#7 are rarely accepted at curbside recycling
        regardless of the symbol.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Each plastic explained
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">#1 PET / PETE. Polyethylene terephthalate</h3>
      <p>
        The most common single-use food and beverage plastic. Considered food-safe
        for single use but problematic when reused or exposed to heat. PET releases
        antimony trioxide (a manufacturing catalyst) at elevated temperatures, and
        is the dominant material in the 2024 Columbia/Rutgers nanoplastic study
        of bottled water (240,000 particles/L).
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">#2 HDPE. High-density polyethylene</h3>
      <p>
        Stiff, opaque plastic used for milk jugs, juice bottles, and detergent
        bottles. One of the lower-leaching food plastics. Still degrades over years
        of UV exposure and sheds microplastic from wear.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">#3 PVC. Polyvinyl chloride</h3>
      <p>
        Avoid for food contact. PVC requires phthalate plasticisers to be flexible,
        and these plasticisers are known endocrine disruptors that leach into food, especially fatty or hot food. PVC was also detected in 12% of carotid
        plaque samples in the NEJM 2024 cardiovascular study.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">#4 LDPE. Low-density polyethylene</h3>
      <p>
        Soft, flexible plastic used for shopping bags, bread bags, and cling film.
        Low chemical leaching, but a major contributor to microfibre and microparticle
        shedding into food and into the environment.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">#5 PP. Polypropylene</h3>
      <p>
        Generally considered the safest of the common food plastics. Heat-tolerant
        (microwave-rated for many products) and chemically stable. <em>Still</em>
        sheds microplastics when microwaved, a 2023 University of Nebraska study
        found a single PP container could release millions of particles per
        microwave cycle.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">#6 PS. Polystyrene</h3>
      <p>
        Avoid for food contact. PS releases styrene under heat or oily food
        contact; the International Agency for Research on Cancer classifies styrene
        as a possible human carcinogen (Group 2A as of 2019). Expanded polystyrene
        (Styrofoam) is especially friable and sheds particles readily.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">#7. Other (often polycarbonate)</h3>
      <p>
        A catch-all category. Older #7 items, especially 5-gallon water cooler
        jugs and some reusable bottles, are polycarbonate, made with bisphenol
        A (BPA), a known endocrine disruptor. &ldquo;BPA-free&rdquo; alternatives
        often use BPS or BPF, which have shown similar hormonal activity. See our{' '}
        <Link href="/blog/microplastics-in-pregnancy" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          BPA-free guide
        </Link>{' '}
        for the full picture.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Quick rules of thumb
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>Never microwave anything in plastic, even #5.</li>
        <li>Never put hot food or liquid into #6 polystyrene.</li>
        <li>Don&apos;t reuse #1 PET water bottles.</li>
        <li>Avoid #3 PVC and #7 polycarbonate for food contact entirely.</li>
        <li>For food storage, glass or stainless steel beats every plastic.</li>
      </ul>

      <p>
        For full reduction strategies see{' '}
        <Link href="/blog/microplastics-in-plastic-containers" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in plastic containers
        </Link>{' '}
        and{' '}
        <Link href="/blog/worst-microplastic-ingredients" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          worst microplastic ingredients
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Material, stainless, glass, ceramic, cast iron, plastic (PE / PP / PS / PVC), silicone, wood.</>,
          <>Visible condition, scratches, chips, warping, fade.</>,
          <>Brand and product line, flags for non-stick / PFAS-treated items.</>,
          <>Use-context flags you log, heat exposure, dishwasher cycles, contact with hot or fatty food.</>,
          <>Cited published research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan kitchen and household products before buying"
        sub="Cookware, food storage, cutting boards, accessories. The app weighs material, condition, brand, and use-context to give a 0–100 risk score per item."
        buttonLabel="Scan household items in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'What plastic numbers are safest for food?',
            answer:
              'Plastics #2 (HDPE), #4 (LDPE), and #5 (PP) are considered the safer common food-contact plastics, with the lowest documented chemical leaching. #1 PET is acceptable for single use only. All plastics shed microplastics under heat, acidity, or wear.',
          },
          {
            question: 'Which plastic numbers should you avoid?',
            answer:
              'Avoid #3 (PVC) for food because of phthalate leaching, #6 (polystyrene) for hot or oily food because of styrene release, and #7 (Other) when the item may contain polycarbonate or BPA. PVC was also linked to cardiovascular events in the 2024 NEJM study.',
          },
          {
            question: 'Is plastic #5 safe to microwave?',
            answer:
              'Polypropylene #5 is the most heat-stable common food plastic and is often labeled microwave-safe. However a 2023 University of Nebraska study found a single PP container can release millions of microplastic particles into food per microwave cycle. Glass is safer.',
          },
          {
            question: 'Is plastic #1 PET safe for water bottles?',
            answer:
              'PET is FDA-approved for single use but is the dominant material in the 2024 nanoplastic study of bottled water (240,000 particles per liter). It is not designed for reuse, refilling, or heating. Glass or stainless steel is safer for repeated use.',
          },
          {
            question: 'What does the recycling number actually mean?',
            answer:
              'The number inside the recycling triangle is a Resin Identification Code (RIC), introduced in 1988 to help sorters at recycling facilities identify the polymer. It is not a safety rating and does not guarantee an item is recyclable in your community.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'ASTM International',
            year: 2013,
            title: 'D7611 Standard Practice for Coding Plastic Manufactured Articles for Resin Identification',
            publisher: 'ASTM',
            url: 'https://www.astm.org/d7611_d7611m-13e01.html',
          },
          {
            authors: 'Hussain KA, Romanova S, Okur I, et al.',
            year: 2023,
            title: 'Assessing the release of microplastics and nanoplastics from plastic containers and reusable food pouches: implications for human health',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c01942',
          },
          {
            authors: 'Marfella R, Prattichizzo F, Sardu C, et al.',
            year: 2024,
            title: 'Microplastics and nanoplastics in atheromas and cardiovascular events',
            publisher: 'New England Journal of Medicine',
            url: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2309822',
          },
          {
            authors: 'IARC Working Group',
            year: 2019,
            title: 'Styrene, Styrene-7,8-oxide, and Quinoline (IARC Monograph 121)',
            publisher: 'International Agency for Research on Cancer',
            url: 'https://publications.iarc.fr/581',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
